import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, LogIn, KeyRound, Ban, RotateCcw, Pencil, Building2, RefreshCw, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { CustomPlanFields, BonificoFields, DEFAULT_CUSTOM_PLAN, toFormPlan } from "@/components/master/CustomPlanFields";
import { BonificoSettingsButton, BonificoStatusPanel, bonificoBadge } from "@/components/master/BonificoControls";

function formatEUR(v) {
  return Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("it-IT"); } catch { return iso; }
}

const STATUS_BADGE = {
  trialing: "bg-blue-500/20 text-blue-300 border-blue-700",
  active: "bg-emerald-500/20 text-emerald-300 border-emerald-700",
  past_due: "bg-amber-500/20 text-amber-300 border-amber-700",
  canceled: "bg-red-500/20 text-red-300 border-red-700",
  expired: "bg-red-500/20 text-red-300 border-red-700",
  pending: "bg-slate-500/20 text-slate-300 border-slate-700",
};

const PLAN_OPTIONS = [
  { id: "base", label: "Base" },
  { id: "pro", label: "Pro" },
  { id: "unlimited", label: "Unlimited" },
  { id: "custom", label: "Personalizzato (bonifico)" },
];

const customPayload = (form) => ({
  ...form,
  bonifico_grace_days: Number(form.bonifico_grace_days ?? 7),
  bonifico_reminder_days: Number(form.bonifico_reminder_days ?? 7),
  custom_plan: { ...form.custom_plan, canone_net: Number(form.custom_plan?.canone_net || 0) },
});

export default function MasterAgenzie() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteForm, setDeleteForm] = useState({ id: "", nome: "", confirm: "" });
  const [deleting, setDeleting] = useState(false);
  const [activeAgency, setActiveAgency] = useState(null);
  const [createForm, setCreateForm] = useState({
    nome_agenzia: "", partita_iva: "", indirizzo: "", codice_sdi: "", pec: "",
    agente_nome: "", agente_cognome: "", agente_email: "", agente_username: "", agente_password: "",
    plan_id: "base", trial_days: 7,
    custom_plan: { ...DEFAULT_CUSTOM_PLAN }, prossima_scadenza: "", bonifico_pagato: false,
    bonifico_grace_days: 7, bonifico_reminder_days: 7,
  });
  const [editForm, setEditForm] = useState({});
  const [pwdForm, setPwdForm] = useState({ user_id: null, label: "", password: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/master/agencies");
      setRows(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const createAgency = async () => {
    try {
      await api.post("/master/agencies", createForm.plan_id === "custom" ? customPayload(createForm) : createForm);
      toast.success("Agenzia creata");
      setCreateOpen(false);
      setCreateForm({ ...createForm, nome_agenzia: "", agente_username: "", agente_password: "", agente_email: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openEdit = async (a) => {
    setActiveAgency(a);
    setEditForm({
      plan_id: a.plan_id || "base",
      plan_status: a.plan_status || "active",
      trial_ends_at: (a.trial_ends_at || "").slice(0, 10),
      current_period_end: (a.current_period_end || "").slice(0, 10),
      master_discount_net: a.master_discount_net || 0,
      master_price_override_net: a.master_price_override_net ?? "",
      custom_plan: toFormPlan(a.custom_plan),
      bonifico_grace_days: a.bonifico_grace_days ?? 7,
      bonifico_reminder_days: a.bonifico_reminder_days ?? 7,
    });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    let payload = { ...editForm };
    if (payload.plan_id === "custom") {
      if (activeAgency?.plan_id !== "custom") {
        if (!payload.prossima_scadenza) { toast.error("Indica la data della prima scadenza"); return; }
        delete payload.plan_status;
      }
      payload = customPayload(payload);
      delete payload.current_period_end;
      delete payload.trial_ends_at;
      delete payload.master_discount_net;
      delete payload.master_price_override_net;
    } else {
      delete payload.custom_plan;
      delete payload.bonifico_grace_days;
      delete payload.bonifico_reminder_days;
    }
    if (payload.plan_id !== "custom") {
      payload.master_discount_net = Number(payload.master_discount_net || 0);
      payload.master_price_override_net = payload.master_price_override_net === "" ? null : Number(payload.master_price_override_net);
    }
    if (payload.trial_ends_at) payload.trial_ends_at = new Date(payload.trial_ends_at).toISOString();
    if (payload.current_period_end) payload.current_period_end = new Date(payload.current_period_end).toISOString();
    try {
      await api.put(`/master/agencies/${activeAgency.id}`, payload);
      toast.success("Agenzia aggiornata");
      setEditOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const suspend = async (a) => {
    if (!window.confirm(`Sospendere l'agenzia "${a.nome_agenzia}"? Gli utenti non potranno più accedere.`)) return;
    try { await api.post(`/master/agencies/${a.id}/suspend`); toast.success("Agenzia sospesa"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const activate = async (a) => {
    try {
      await api.post(`/master/agencies/${a.id}/activate`, { days: 30 });
      toast.success("Agenzia riattivata (+30 giorni)");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const impersonate = async (a) => {
    if (!window.confirm(`Accedere come agente di "${a.nome_agenzia}"? L'azione verrà registrata in audit.`)) return;
    try {
      const r = await api.post(`/master/agencies/${a.id}/impersonate`);
      localStorage.setItem("access_token", r.data.access_token);
      localStorage.setItem("__impersonated_from_master", "1");
      window.location.href = "/admin";
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openPwd = (u, agencyName) => {
    setPwdForm({ user_id: u.id, label: `${u.username} (${agencyName})`, password: "" });
    setPwdOpen(true);
  };
  const submitPwd = async () => {
    if (pwdForm.password.length < 6) { toast.error("Min 6 caratteri"); return; }
    try {
      await api.post(`/master/users/${pwdForm.user_id}/reset-password`, { password: pwdForm.password });
      toast.success("Password aggiornata");
      setPwdOpen(false);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openDelete = (a) => {
    setDeleteForm({ id: a.id, nome: a.nome_agenzia || "", confirm: "" });
    setDeleteOpen(true);
  };
  const submitDelete = async () => {
    if (deleteForm.confirm !== deleteForm.nome) {
      toast.error("Il nome inserito non corrisponde");
      return;
    }
    setDeleting(true);
    try {
      const r = await api.delete(`/master/agencies/${deleteForm.id}`, {
        data: { confirm_nome_agenzia: deleteForm.confirm },
      });
      const total = Object.values(r.data.deleted_counts || {}).reduce((s, n) => s + (n > 0 ? n : 0), 0);
      toast.success(`Agenzia eliminata definitivamente (${total} record correlati rimossi)`);
      setDeleteOpen(false);
      setDeleteForm({ id: "", nome: "", confirm: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setDeleting(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-amber-400 font-semibold">Gestione</div>
          <h1 className="font-heading text-3xl font-bold">Agenzie</h1>
        </div>
        <div className="flex gap-2">
          <BonificoSettingsButton />
          <Button variant="outline" onClick={load} className="border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800" data-testid="master-reload">
            <RefreshCw className="w-3.5 h-3.5 mr-2" /> Aggiorna
          </Button>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold" data-testid="master-create-agency">
                <Plus className="w-4 h-4 mr-2" /> Nuova agenzia
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Crea nuova agenzia</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Nome agenzia *</Label><Input value={createForm.nome_agenzia} onChange={e => setCreateForm({ ...createForm, nome_agenzia: e.target.value })} data-testid="create-nome" /></div>
                <div><Label>Partita IVA</Label><Input value={createForm.partita_iva} onChange={e => setCreateForm({ ...createForm, partita_iva: e.target.value })} /></div>
                <div><Label>Codice SDI</Label><Input value={createForm.codice_sdi} onChange={e => setCreateForm({ ...createForm, codice_sdi: e.target.value })} /></div>
                <div className="col-span-2"><Label>Indirizzo</Label><Input value={createForm.indirizzo} onChange={e => setCreateForm({ ...createForm, indirizzo: e.target.value })} /></div>
                <div className="col-span-2"><Label>PEC</Label><Input value={createForm.pec} onChange={e => setCreateForm({ ...createForm, pec: e.target.value })} /></div>
                <div className="col-span-2 border-t border-slate-200 pt-3"><div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Agente titolare</div></div>
                <div><Label>Nome *</Label><Input value={createForm.agente_nome} onChange={e => setCreateForm({ ...createForm, agente_nome: e.target.value })} /></div>
                <div><Label>Cognome *</Label><Input value={createForm.agente_cognome} onChange={e => setCreateForm({ ...createForm, agente_cognome: e.target.value })} /></div>
                <div><Label>Email *</Label><Input type="email" value={createForm.agente_email} onChange={e => setCreateForm({ ...createForm, agente_email: e.target.value })} /></div>
                <div><Label>Username *</Label><Input value={createForm.agente_username} onChange={e => setCreateForm({ ...createForm, agente_username: e.target.value })} data-testid="create-username" /></div>
                <div className="col-span-2"><Label>Password *</Label><Input value={createForm.agente_password} onChange={e => setCreateForm({ ...createForm, agente_password: e.target.value })} data-testid="create-password" /></div>
                <div className="col-span-2 border-t border-slate-200 pt-3"><div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Abbonamento</div></div>
                <div>
                  <Label>Piano</Label>
                  <select className="w-full border border-slate-200 rounded-md h-10 px-2 text-sm" value={createForm.plan_id} onChange={e => setCreateForm({ ...createForm, plan_id: e.target.value })} data-testid="create-plan-select">
                    {PLAN_OPTIONS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                  </select>
                </div>
                {createForm.plan_id === "custom" ? (
                  <>
                    <div className="text-xs text-slate-500 self-end pb-2">Pagamento tramite bonifico, nessun periodo di prova.</div>
                    <CustomPlanFields plan={createForm.custom_plan} onChange={(cp) => setCreateForm({ ...createForm, custom_plan: cp })} />
                    <BonificoFields form={createForm} onChange={setCreateForm} />
                  </>
                ) : (
                  <div><Label>Giorni di prova</Label><Input type="number" min="0" value={createForm.trial_days} onChange={e => setCreateForm({ ...createForm, trial_days: e.target.value })} /></div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateOpen(false)}>Annulla</Button>
                <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={createAgency} data-testid="create-submit">Crea agenzia</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="bg-slate-900 border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-800/60 text-left">
                <th className="px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Agenzia</th>
                <th className="px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Agente</th>
                <th className="px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Piano</th>
                <th className="px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Stato</th>
                <th className="px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Scadenza</th>
                <th className="px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400 text-right">Utenti</th>
                <th className="px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400 text-right">€ / mese</th>
                <th className="px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading && <tr><td colSpan={8} className="text-center text-slate-400 py-6">Caricamento…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-6">Nessuna agenzia</td></tr>}
              {rows.map((a) => {
                const isSuspended = a.plan_status === "canceled" || a.plan_status === "expired";
                return (
                  <tr key={a.id} className="hover:bg-slate-800/40" data-testid={`master-row-${a.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-100 flex items-center gap-2">
                        <Building2 className="w-3.5 h-3.5 text-slate-400" />
                        {a.nome_agenzia}
                      </div>
                      <div className="text-[10px] text-slate-500">{a.partita_iva || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-300">
                      {a.owner ? (
                        <>
                          <div>{a.owner.nome} {a.owner.cognome}</div>
                          <div className="text-[10px] text-slate-500">{a.owner.email}</div>
                        </>
                      ) : <span className="text-slate-500">—</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-200">
                      {a.plan_name}
                      {a.plan_id === "custom" && (
                        <div className="mt-1"><Badge variant="outline" className={`text-[10px] ${bonificoBadge(a).cls}`} data-testid={`master-bonifico-badge-${a.id}`}>{bonificoBadge(a).label}</Badge></div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={STATUS_BADGE[a.plan_status] || STATUS_BADGE.pending}>{a.plan_status}</Badge>
                    </td>
                    <td className="px-4 py-3 text-slate-300">{formatDate(a.current_period_end)}</td>
                    <td className="px-4 py-3 text-right font-mono-num text-slate-200">{a.n_users}</td>
                    <td className="px-4 py-3 text-right font-mono-num text-emerald-400 font-semibold">{formatEUR(a.effective_price_net)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-amber-300 hover:bg-slate-700" title="Entra come agente" onClick={() => impersonate(a)} data-testid={`master-impersonate-${a.id}`}><LogIn className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-300 hover:bg-slate-700" title="Modifica piano / sconto" onClick={() => openEdit(a)} data-testid={`master-edit-${a.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        {a.owner && <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-300 hover:bg-slate-700" title="Reset password agente" onClick={() => openPwd(a.owner, a.nome_agenzia)} data-testid={`master-pwd-${a.id}`}><KeyRound className="w-3.5 h-3.5" /></Button>}
                        {isSuspended ? (
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-300 hover:bg-slate-700" title="Riattiva (+30gg)" onClick={() => activate(a)} data-testid={`master-activate-${a.id}`}><RotateCcw className="w-3.5 h-3.5" /></Button>
                        ) : (
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:bg-slate-700" title="Sospendi" onClick={() => suspend(a)} data-testid={`master-suspend-${a.id}`}><Ban className="w-3.5 h-3.5" /></Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:bg-red-950 hover:text-red-300" title="Elimina definitivamente" onClick={() => openDelete(a)} data-testid={`master-delete-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifica agenzia — {activeAgency?.nome_agenzia}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Piano</Label>
              <select className="w-full border border-slate-200 rounded-md h-10 px-2 text-sm" value={editForm.plan_id || "base"} onChange={e => setEditForm({ ...editForm, plan_id: e.target.value })} data-testid="edit-plan-select">
                {PLAN_OPTIONS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Stato</Label>
              <select className="w-full border border-slate-200 rounded-md h-10 px-2 text-sm" value={editForm.plan_status || "active"} onChange={e => setEditForm({ ...editForm, plan_status: e.target.value })}>
                <option value="trialing">In prova</option>
                <option value="active">Attiva</option>
                <option value="past_due">Pagamento in sospeso</option>
                <option value="canceled">Sospesa</option>
                <option value="expired">Scaduta</option>
              </select>
            </div>
            {editForm.plan_id === "custom" ? (
              <>
                <CustomPlanFields plan={editForm.custom_plan} onChange={(cp) => setEditForm({ ...editForm, custom_plan: cp })} />
                {activeAgency?.plan_id === "custom" ? (
                  <BonificoStatusPanel agency={activeAgency} onUpdated={(a) => { setActiveAgency({ ...activeAgency, ...a }); load(); }} />
                ) : (
                  <div className="col-span-2"><Label>Prima scadenza *</Label><Input type="date" value={editForm.prossima_scadenza || ""} onChange={e => setEditForm({ ...editForm, prossima_scadenza: e.target.value })} data-testid="edit-first-due" /></div>
                )}
                <div><Label>Giorni di tolleranza</Label><Input type="number" min="0" value={editForm.bonifico_grace_days} onChange={e => setEditForm({ ...editForm, bonifico_grace_days: e.target.value })} data-testid="edit-grace" /></div>
                <div><Label>Promemoria (giorni prima)</Label><Input type="number" min="0" value={editForm.bonifico_reminder_days} onChange={e => setEditForm({ ...editForm, bonifico_reminder_days: e.target.value })} data-testid="edit-reminder" /></div>
              </>
            ) : (
              <>
                {activeAgency?.plan_id === "custom" && (
                  <div className="col-span-2 rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900" data-testid="edit-back-to-standard-hint">
                    Tornando a un piano standard il pagamento passa a Stripe: imposta lo stato (es. &quot;In prova&quot;) e le date, così l&apos;agente potrà attivare l&apos;abbonamento con carta.
                  </div>
                )}
                <div><Label>Fine trial</Label><Input type="date" value={editForm.trial_ends_at || ""} onChange={e => setEditForm({ ...editForm, trial_ends_at: e.target.value })} /></div>
                <div><Label>Prossima scadenza</Label><Input type="date" value={editForm.current_period_end || ""} onChange={e => setEditForm({ ...editForm, current_period_end: e.target.value })} /></div>
                <div className="col-span-2 border-t border-slate-200 pt-3"><div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Override pricing</div></div>
                <div><Label>Sconto fisso (netto, €)</Label><Input type="number" step="0.01" value={editForm.master_discount_net || ""} onChange={e => setEditForm({ ...editForm, master_discount_net: e.target.value })} /></div>
                <div><Label>Prezzo override netto (€)</Label><Input type="number" step="0.01" placeholder="lascia vuoto per usare il piano" value={editForm.master_price_override_net ?? ""} onChange={e => setEditForm({ ...editForm, master_price_override_net: e.target.value })} /></div>
                <div className="col-span-2 text-xs text-slate-500">Se imposti un override, sostituisce il prezzo del piano. Altrimenti viene applicato lo sconto.</div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annulla</Button>
            <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={saveEdit} data-testid="master-edit-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset password</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="text-sm text-slate-600">Utente: <strong>{pwdForm.label}</strong></div>
            <Label>Nuova password</Label>
            <Input value={pwdForm.password} onChange={e => setPwdForm({ ...pwdForm, password: e.target.value })} data-testid="master-pwd-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdOpen(false)}>Annulla</Button>
            <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={submitPwd} data-testid="master-pwd-save">Aggiorna password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteOpen} onOpenChange={(v) => { if (!v) { setDeleteOpen(false); setDeleteForm({ id: "", nome: "", confirm: "" }); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Elimina definitivamente l&apos;agenzia
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900">
              Stai per eliminare in modo <strong>irreversibile</strong> l&apos;agenzia <strong>{deleteForm.nome}</strong> e <strong>tutti</strong> i dati collegati: utenze, fogli cassa, incassi, versamenti, bozze, distinte, spese, compagnie, impostazioni, log email e transazioni di pagamento.
              <br /><br />Non sarà possibile recuperare i dati eliminati.
            </div>
            <div>
              <Label className="text-slate-700">Per confermare, digita esattamente il nome dell&apos;agenzia:</Label>
              <div className="text-xs text-slate-500 mb-1">Atteso: <code className="bg-slate-100 px-1.5 py-0.5 rounded">{deleteForm.nome}</code></div>
              <Input
                value={deleteForm.confirm}
                onChange={(e) => setDeleteForm({ ...deleteForm, confirm: e.target.value })}
                placeholder="Digita il nome dell'agenzia"
                data-testid="master-delete-confirm-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Annulla</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={submitDelete}
              disabled={deleting || deleteForm.confirm !== deleteForm.nome}
              data-testid="master-delete-confirm"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              {deleting ? "Eliminazione…" : "Elimina definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
